import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { axiosGateway } from "../../../../api/axios";
import ENDPOINTS from "../../../../api/endpoints";
import { createConsultation } from "../../../../consultation/redux/thunks/consultationThunks";
import { processDocument } from "../../../../medicalRecord/redux/thunks/extractionThunks";
import {
  fetchNephrologyExam,
  fetchNephrologyHistory,
  fetchNephrologyUrinaryAndUremia,
  saveNephrologyExam,
  saveNephrologyHistory,
} from "../../redux/thunks/nephrologyHistoryThunks";
import {
  selectNephrologyExamByPatientId,
  selectNephrologyExamFetchError,
  selectNephrologyExamFetchStatus,
  selectNephrologyHistoryByPatientId,
  selectNephrologyHistoryFetchError,
  selectNephrologyHistoryFetchStatus,
  selectNephrologyUrinaryAndUremiaByPatientId,
  selectNephrologyUrinaryAndUremiaFetchError,
  selectNephrologyUrinaryAndUremiaFetchStatus,
} from "../../redux/selectors/nephrologySelectors";
import { collectNephrologySubmission } from "../../nephrologySubmission";

import "./NeurologueConsultationPage.css";

import ReasonAndHistorySection from "../../componants/ReasonAndHistorySection/ReasonAndHistorySection";
import VitalsAndExamSection from "../../componants/VitalsAndExamSection/VitalsAndExamSection";
import UrineAndUremiaSection from "../../componants/UrineAndUremiaSection/UrineAndUremiaSection";
import RenalImagingSection from "../../componants/RenalImagingSection/RenalImagingSection";
import BiologyAndCkdSection from "../../componants/BiologyAndCkdSection/BiologyAndCkdSection";
import NephroprotectionAndRrtSection from "../../componants/NephroprotectionAndRrtSection/NephroprotectionAndRrtSection";
import SummaryAndPlanSection from "../../componants/SummaryAndPlanSection/SummaryAndPlanSection";
import "./NeurologueConsultationPage.theme.css";

const getFieldValue = (field) => {
  if (field.matches("[role='switch']")) {
    return `@checked:${field.getAttribute("aria-checked") === "true"}`;
  }
  if (field.matches("[aria-pressed]")) {
    return `@pressed:${field.getAttribute("aria-pressed") === "true"}`;
  }
  if (field instanceof HTMLInputElement && ["checkbox", "radio"].includes(field.type)) {
    return `@checked:${field.checked}`;
  }
  return String(field.value ?? "").trim();
};

const getDefaultFieldValue = (field) => {
  if (field.matches("[role='switch'], [aria-pressed]")) return getFieldValue(field);
  if (field instanceof HTMLInputElement && ["checkbox", "radio"].includes(field.type)) {
    return `@checked:${field.defaultChecked}`;
  }
  if (field instanceof HTMLSelectElement) {
    return field.options[field.selectedIndex]?.defaultSelected
      ? field.value
      : (Array.from(field.options).find((option) => option.defaultSelected)?.value ?? "");
  }
  return String(field.defaultValue ?? "").trim();
};

const hasFieldValue = (value) =>
  typeof value === "string" && value.startsWith("@")
    ? true
    : String(value ?? "").trim().length > 0;

export default function NeurologueConsultationPage({
  userId,
  medicalInfo = null,
  patientMeasurements = null,
  doctorId,
  subprofileId,
  onSuccess,
  onOpenPrescribePopup,
}) {
  const { t } = useTranslation("neurologue");
  const dispatch = useDispatch();
  const cleanUserId = String(userId || "").trim();
  const historyStatus = useSelector((state) => selectNephrologyHistoryFetchStatus(state, cleanUserId));
  const historyError = useSelector((state) => selectNephrologyHistoryFetchError(state, cleanUserId));
  const historyRecords = useSelector((state) => selectNephrologyHistoryByPatientId(state, cleanUserId));
  const examRecords = useSelector((state) => selectNephrologyExamByPatientId(state, cleanUserId));
  const examStatus = useSelector((state) => selectNephrologyExamFetchStatus(state, cleanUserId));
  const examError = useSelector((state) => selectNephrologyExamFetchError(state, cleanUserId));
  const examRecord = examRecords[0] || null;
  const urinaryRecords = useSelector((state) => selectNephrologyUrinaryAndUremiaByPatientId(state, cleanUserId));
  const urinaryStatus = useSelector((state) => selectNephrologyUrinaryAndUremiaFetchStatus(state, cleanUserId));
  const urinaryError = useSelector((state) => selectNephrologyUrinaryAndUremiaFetchError(state, cleanUserId));
  const urinaryRecord = urinaryRecords[0] || null;
  const contentRef = useRef(null);
  const submissionRef = useRef(null);
  const [openSections, setOpenSections] = useState(() => new Set(["reason-history"]));
  const [activeSection, setActiveSection] = useState("reason-history");
  const [completedSections, setCompletedSections] = useState({});
  const [isSavingConsultation, setIsSavingConsultation] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [imagingRecords, setImagingRecords] = useState([]);
  const [imagingStatus, setImagingStatus] = useState("idle");
  const [imagingError, setImagingError] = useState("");
  const [biologicalRecords, setBiologicalRecords] = useState([]);
  const [biologicalStatus, setBiologicalStatus] = useState("idle");
  const [biologicalError, setBiologicalError] = useState("");
  const biologicalRecord = biologicalRecords[0] || null;
  const [nephroprotectionRecords, setNephroprotectionRecords] = useState([]);
  const [nephroprotectionStatus, setNephroprotectionStatus] = useState("idle");
  const [nephroprotectionError, setNephroprotectionError] = useState("");
  const nephroprotectionRecord = nephroprotectionRecords[0] || null;
  const [summaryRecords, setSummaryRecords] = useState([]);
  const [summaryStatus, setSummaryStatus] = useState("idle");
  const [summaryError, setSummaryError] = useState("");
  const summaryRecord = summaryRecords[0] || null;
  const initialFieldValues = useRef(new WeakMap());
  const completionFrame = useRef(null);
  const sections = [
    { id: "reason-history", title: t("sections.reason"), Component: ReasonAndHistorySection },
    { id: "vitals-exam", title: t("sections.vitals"), Component: VitalsAndExamSection },
    { id: "urine-uremia", title: t("sections.urine"), Component: UrineAndUremiaSection },
    { id: "renal-imaging", title: t("sections.imaging"), Component: RenalImagingSection },
    { id: "biology-ckd", title: t("sections.biology"), Component: BiologyAndCkdSection },
    { id: "nephroprotection-rrt", title: t("sections.nephro"), Component: NephroprotectionAndRrtSection },
    { id: "summary-plan", title: t("sections.summary"), Component: SummaryAndPlanSection },
  ];

  useEffect(() => {
    if (!cleanUserId) return;
    let active = true;
    submissionRef.current = null;
    dispatch(fetchNephrologyHistory(cleanUserId));
    dispatch(fetchNephrologyExam(cleanUserId));
    dispatch(fetchNephrologyUrinaryAndUremia(cleanUserId));
    setImagingRecords([]);
    setImagingStatus("loading");
    setImagingError("");
    setBiologicalRecords([]);
    setBiologicalStatus("loading");
    setBiologicalError("");
    setNephroprotectionRecords([]);
    setNephroprotectionStatus("loading");
    setNephroprotectionError("");
    setSummaryRecords([]);
    setSummaryStatus("loading");
    setSummaryError("");
    axiosGateway.get(
      `/nephrology${ENDPOINTS.SPECIALITY.NEPHROLOGY.SUMMARY_BY_PATIENT(cleanUserId)}`
    ).then(({ data }) => {
      if (!active) return;
      const records = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setSummaryRecords(records);
      setSummaryStatus("succeeded");
    }).catch((error) => {
      if (!active) return;
      setSummaryError(String(error?.response?.data?.message || error?.message || "Unable to load consultation summaries"));
      setSummaryStatus("failed");
    });
    axiosGateway.get(
      `/nephrology${ENDPOINTS.SPECIALITY.NEPHROLOGY.NEPHROPROTECTION_BY_PATIENT(cleanUserId)}`
    ).then(({ data }) => {
      if (!active) return;
      const records = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setNephroprotectionRecords(records);
      setNephroprotectionStatus("succeeded");
    }).catch((error) => {
      if (!active) return;
      setNephroprotectionError(String(error?.response?.data?.message || error?.message || "Unable to load nephroprotection data"));
      setNephroprotectionStatus("failed");
    });
    axiosGateway.get(
      `/nephrology${ENDPOINTS.SPECIALITY.NEPHROLOGY.BIOLOGICAL_FOLLOW_UP_BY_PATIENT(cleanUserId)}`
    ).then(({ data }) => {
      if (!active) return;
      const records = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setBiologicalRecords(records);
      setBiologicalStatus("succeeded");
    }).catch((error) => {
      if (!active) return;
      setBiologicalError(String(error?.response?.data?.message || error?.message || "Unable to load biological follow-up"));
      setBiologicalStatus("failed");
    });
    axiosGateway.get(
      `/nephrology${ENDPOINTS.SPECIALITY.NEPHROLOGY.IMAGING_BY_PATIENT(cleanUserId)}`
    ).then(({ data }) => {
      if (!active) return;
      const records = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setImagingRecords(records);
      setImagingStatus("succeeded");
    }).catch((error) => {
      if (!active) return;
      setImagingError(String(error?.response?.data?.message || error?.message || "Unable to load imaging records"));
      setImagingStatus("failed");
    });
    return () => { active = false; };
  }, [cleanUserId, dispatch]);

  useEffect(() => {
    if (summaryStatus !== "succeeded") return undefined;
    const frame = requestAnimationFrame(() => {
      const panel = document.getElementById("summary-plan-content");
      panel?.querySelectorAll("input, textarea, select, [aria-pressed], [role='switch']").forEach((field) => {
        initialFieldValues.current.set(field, getFieldValue(field));
      });
      refreshCompletedSections();
    });
    return () => cancelAnimationFrame(frame);
  }, [cleanUserId, summaryRecord?.id, summaryStatus]);

  useEffect(() => {
    if (nephroprotectionStatus !== "succeeded") return undefined;
    const frame = requestAnimationFrame(() => {
      const panel = document.getElementById("nephroprotection-rrt-content");
      panel?.querySelectorAll("input, textarea, select, [aria-pressed], [role='switch']").forEach((field) => {
        initialFieldValues.current.set(field, getFieldValue(field));
      });
      refreshCompletedSections();
    });
    return () => cancelAnimationFrame(frame);
  }, [cleanUserId, nephroprotectionRecord?.id, nephroprotectionStatus]);

  useEffect(() => {
    if (historyStatus !== "succeeded") return undefined;
    const frame = requestAnimationFrame(() => {
      const panel = document.getElementById("reason-history-content");
      panel?.querySelectorAll("input, textarea, select, [aria-pressed], [role='switch']").forEach((field) => {
        initialFieldValues.current.set(field, getFieldValue(field));
      });
      refreshCompletedSections();
    });
    return () => cancelAnimationFrame(frame);
  }, [cleanUserId, historyStatus]);

  useEffect(() => {
    if (examStatus !== "succeeded") return undefined;
    const frame = requestAnimationFrame(() => {
      const panel = document.getElementById("vitals-exam-content");
      panel?.querySelectorAll("input, textarea, select, [aria-pressed], [role='switch']").forEach((field) => {
        initialFieldValues.current.set(field, getFieldValue(field));
      });
      refreshCompletedSections();
    });
    return () => cancelAnimationFrame(frame);
  }, [cleanUserId, examRecord, examStatus, medicalInfo, patientMeasurements]);

  useEffect(() => {
    if (biologicalStatus !== "succeeded") return undefined;
    const frame = requestAnimationFrame(() => {
      const panel = document.getElementById("biology-ckd-content");
      panel?.querySelectorAll("input, textarea, select, [aria-pressed], [role='switch']").forEach((field) => {
        initialFieldValues.current.set(field, getFieldValue(field));
      });
      refreshCompletedSections();
    });
    return () => cancelAnimationFrame(frame);
  }, [cleanUserId, biologicalRecord, biologicalStatus]);

  useEffect(() => {
    if (urinaryStatus !== "succeeded") return undefined;
    const frame = requestAnimationFrame(() => {
      const panel = document.getElementById("urine-uremia-content");
      panel?.querySelectorAll("input, textarea, select, [aria-pressed], [role='switch']").forEach((field) => {
        initialFieldValues.current.set(field, getFieldValue(field));
      });
      refreshCompletedSections();
    });
    return () => cancelAnimationFrame(frame);
  }, [cleanUserId, urinaryRecord?.id, urinaryStatus]);

  useEffect(() => {
    if (imagingStatus !== "succeeded") return undefined;
    const frame = requestAnimationFrame(() => {
      const panel = document.getElementById("renal-imaging-content");
      panel?.querySelectorAll("[data-imaging-date], [data-imaging-report]").forEach((field) => {
        initialFieldValues.current.set(field, getFieldValue(field));
      });
      refreshCompletedSections();
    });
    return () => cancelAnimationFrame(frame);
  }, [cleanUserId, imagingRecords, imagingStatus]);

  useEffect(() => {
    sections.forEach(({ id }) => {
      const panel = document.getElementById(`${id}-content`);
      panel?.querySelectorAll("input, textarea, select, [aria-pressed], [role='switch']").forEach((field) => {
        initialFieldValues.current.set(field, getFieldValue(field));
      });
    });
  }, []);

  const refreshCompletedSections = () => {
    const next = {};
    sections.forEach(({ id }) => {
      const panel = document.getElementById(`${id}-content`);
      const fields = panel?.querySelectorAll("input, textarea, select, [aria-pressed], [role='switch']") || [];
      next[id] = Array.from(fields).some((field) => {
        const currentValue = getFieldValue(field);
        const initialValue = initialFieldValues.current.has(field)
          ? initialFieldValues.current.get(field)
          : getDefaultFieldValue(field);
        return hasFieldValue(currentValue) && currentValue !== initialValue;
      });
    });
    setCompletedSections(next);
  };

  const scheduleCompletionRefresh = () => {
    if (completionFrame.current !== null) cancelAnimationFrame(completionFrame.current);
    completionFrame.current = requestAnimationFrame(() => {
      refreshCompletedSections();
      completionFrame.current = null;
    });
  };

  const openSectionAndScroll = (id) => {
    setActiveSection(id);
    setOpenSections((current) => new Set(current).add(id));
    requestAnimationFrame(() => {
      document
        .getElementById(`${id}-content`)
        ?.closest(".neurologue-accordion__item")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const openPrescriptionPopup = async () => {
    const cleanDoctorId = String(doctorId || "").trim();
    if (!cleanUserId || !cleanDoctorId) {
      setSubmissionError(t("submission.missingIdentity", {
        defaultValue: "Le patient ou le médecin n’est pas identifié.",
      }));
      return;
    }
    if (historyStatus === "loading" || examStatus === "loading" || urinaryStatus === "loading" || imagingStatus === "loading" || biologicalStatus === "loading" || nephroprotectionStatus === "loading" || summaryStatus === "loading") {
      setSubmissionError(t("common.loading", { defaultValue: "Attendez le chargement des données avant d’enregistrer." }));
      return;
    }
    if (urinaryStatus === "failed") {
      setSubmissionError(String(urinaryError || t("urine.loadFailed", {
        defaultValue: "Impossible de charger les dernières données urinaires. Réessayez avant d’enregistrer.",
      })));
      return;
    }
    if (imagingStatus === "failed") {
      setSubmissionError(String(imagingError || t("imaging.loadFailed", {
        defaultValue: "Impossible de charger les examens d’imagerie enregistrés. Réessayez avant d’enregistrer.",
      })));
      return;
    }
    if (biologicalStatus === "failed") {
      setSubmissionError(String(biologicalError || t("biology.loadFailed", {
        defaultValue: "Impossible de charger le suivi biologique enregistré. Réessayez avant d’enregistrer.",
      })));
      return;
    }
    if (nephroprotectionStatus === "failed") {
      setSubmissionError(String(nephroprotectionError || t("nephro.loadFailed", {
        defaultValue: "Impossible de charger les données de néphroprotection enregistrées. Réessayez avant d’enregistrer.",
      })));
      return;
    }
    if (summaryStatus === "failed") {
      setSubmissionError(String(summaryError || t("summary.loadFailed", {
        defaultValue: "Impossible de charger la synthèse précédente. Réessayez avant d’enregistrer.",
      })));
      return;
    }
    const isFieldChanged = (field) => {
      if (!field) return false;
      const initialValue = initialFieldValues.current.has(field)
        ? initialFieldValues.current.get(field)
        : getDefaultFieldValue(field);
      return getFieldValue(field) !== initialValue;
    };
    const data = collectNephrologySubmission(contentRef.current, isFieldChanged);
    if (data.validationErrors.length) {
      setSubmissionError(data.validationErrors.join(" "));
      return;
    }
    if (!Object.keys(data.payloads).length && !data.imaging.length && !Object.values(data.summary).some(Boolean)) {
      setSubmissionError(t("submission.empty", {
        defaultValue: "Ajoutez au moins une information avant d’enregistrer la consultation.",
      }));
      return;
    }
    setSubmissionError("");
    setIsSavingConsultation(true);
    try {
      const consultationSections = data.consultationSections;
      const attempt = submissionRef.current;
      let consultationId = attempt?.consultationId;
      if (!consultationId) {
        const created = await dispatch(createConsultation({
          userId: cleanUserId,
          doctorId: cleanDoctorId,
          ...(subprofileId ? { subprofileId } : {}),
          consultationDateTime: new Date().toISOString(),
          title: data.reasonSummary || "Consultation néphrologique",
          sections: consultationSections,
        })).unwrap();
        consultationId = String(created?.id || created?._id || created?.consultation?.id || "").trim();
        if (!consultationId) throw new Error("La consultation a été créée sans identifiant retourné.");
        submissionRef.current = { consultationId, savedResources: {}, uploadedDocuments: {} };
      }
      const savedResources = submissionRef.current.savedResources;
      const uploadedDocuments = submissionRef.current.uploadedDocuments;
      for (const [resource, payload] of Object.entries(data.payloads)) {
        if (resource === "history" || resource === "exam") continue;
        const body = payload;
        const savedId = savedResources[resource]
          || (resource === "nephroprotection" ? nephroprotectionRecord?.id : undefined);
        const response = savedId
          ? await axiosGateway.patch(`/nephrology/${resource}/${encodeURIComponent(savedId)}`, body)
          : await axiosGateway.post(
              `/nephrology/patients/${encodeURIComponent(cleanUserId)}/${resource}`,
              body,
            );
        const recordId = response.data?.id || response.data?._id;
        if (!savedId && recordId) savedResources[resource] = String(recordId);
      }
      if (data.payloads.history) {
        const savedHistory = await dispatch(saveNephrologyHistory({
          patientId: cleanUserId,
          historyId: savedResources.history,
          values: data.payloads.history,
        })).unwrap();
        const historyId = savedHistory?.record?.id || savedHistory?.record?._id;
        if (historyId) savedResources.history = String(historyId);
      }
      if (data.payloads.exam) {
        const savedExam = await dispatch(saveNephrologyExam({
          patientId: cleanUserId,
          examId: savedResources.exam || examRecord?.id,
          values: data.payloads.exam,
        })).unwrap();
        const examId = savedExam?.record?.id || savedExam?.record?._id;
        if (examId) savedResources.exam = String(examId);
      }
      for (const [index, item] of data.imaging.entries()) {
        if (!item.file && !item.documentId) continue;
        const resourceKey = `imaging-${item.imagingType || index}`;
        const savedId = savedResources[resourceKey];
        let documentId = uploadedDocuments[resourceKey] || item.documentId;
        if (!savedId) {
          if (item.file && !uploadedDocuments[resourceKey]) {
            const document = await dispatch(processDocument({
              file: item.file,
              userId: cleanUserId,
              subprofileId,
            })).unwrap();
            documentId = String(
              document?.document_id || document?.documentId || document?.id_doc || document?._id || document?.id || "",
            ).trim();
            if (!documentId) throw new Error(`L’upload du document « ${item.file.name} » n’a pas retourné d’identifiant.`);
            uploadedDocuments[resourceKey] = documentId;
          }
        }
        const body = {
          imagingType: item.imagingType,
          description: item.description,
          ...(item.dateDescription ? { dateDescription: new Date(item.dateDescription).toISOString() } : {}),
        };
        if (!documentId) throw new Error("Le document sélectionné n’a pas retourné d’identifiant valide.");
        const response = savedId
          ? await axiosGateway.patch(`/nephrology/imaging/${encodeURIComponent(savedId)}`, body)
          : await axiosGateway.post(
              `/nephrology/patients/${encodeURIComponent(cleanUserId)}/imaging`,
              body,
              { headers: { "x-document-id": documentId } },
            );
        const recordId = response.data?.id || response.data?._id;
        if (!savedId && recordId) savedResources[resourceKey] = String(recordId);
      }
      await onSuccess?.(consultationId);
      const diagnosticText = [data.summary.diagnosis, data.summary.summary, data.summary.plan]
        .filter(Boolean)
        .join("\n\n");
      onOpenPrescribePopup?.(consultationId, {
        frontOnly: false,
        consultationId,
        diagnosticText,
      });
    } catch (error) {
      const message = error?.response?.data?.message || error?.message ||
        t("submission.failed", { defaultValue: "Impossible d’enregistrer la consultation néphrologique." });
      setSubmissionError(Array.isArray(message) ? message.join(", ") : String(message));
    } finally {
      setIsSavingConsultation(false);
    }
  };
  return (
    <div className="neurologue-page">
      <div
        ref={contentRef}
        className="neurologue-page__content"
        onInputCapture={scheduleCompletionRefresh}
        onChangeCapture={scheduleCompletionRefresh}
        onClickCapture={scheduleCompletionRefresh}
      >
        <nav className="neurologue-step-nav" aria-label={t("page.sectionNavigation", { defaultValue: "Consultation sections" })}>
          {sections.map(({ id, title }, index) => {
            const isActive = activeSection === id;
            const isComplete = Boolean(completedSections[id]);
            return (
              <button
                key={id}
                type="button"
                className={`neurologue-step-nav__button${isActive ? " is-active" : ""}${isComplete ? " is-complete" : ""}`}
                aria-current={isActive ? "step" : undefined}
                aria-controls={`${id}-content`}
                onClick={() => openSectionAndScroll(id)}
              >
                <span className="neurologue-step-nav__number" aria-hidden="true">
                  {isComplete ? "✓" : index + 1}
                </span>
                <span>{title}</span>
              </button>
            );
          })}
        </nav>

        <div className="neurologue-accordion">
          {sections.map(({ id, title, Component }) => {
            const isOpen = openSections.has(id);
            return (
              <section
                className={`neurologue-accordion__item${isOpen ? " is-open" : ""}`}
                key={id}
              >
                <button
                  type="button"
                  className="neurologue-accordion__trigger"
                  aria-expanded={isOpen}
                  aria-controls={`${id}-content`}
                  onClick={() => {
                    setActiveSection(id);
                    setOpenSections((current) => {
                      const next = new Set(current);
                      if (next.has(id)) next.delete(id);
                      else next.add(id);
                      return next;
                    });
                  }}
                >
                  <span className="neurologue-accordion__status" aria-hidden="true" />
                  <span className="neurologue-accordion__title">{title}</span>
                  <span className="neurologue-accordion__hint">{isOpen ? t("page.close") : t("page.open")}</span>
                  <span className="neurologue-accordion__chevron" aria-hidden="true" />
                </button>
                <div className="neurologue-accordion__content" id={`${id}-content`} hidden={!isOpen}>
                  <Component
                    {...(id === "reason-history"
                      ? { patientId: cleanUserId, historyRecords, historyLoading: historyStatus === "loading", historyError }
                        : id === "vitals-exam"
                        ? { examRecord, examLoading: examStatus === "loading", examError, userId: cleanUserId, medicalInfo, patientMeasurements }
                      : id === "urine-uremia"
                        ? { patientId: cleanUserId, urinaryRecord, urinaryLoading: urinaryStatus === "loading", urinaryError }
                      : id === "renal-imaging"
                        ? { patientId: cleanUserId, imagingRecords, imagingLoading: imagingStatus === "loading", imagingError }
                      : id === "biology-ckd"
                        ? {
                            patientId: cleanUserId,
                            biologicalRecord,
                            biologicalLoading: biologicalStatus === "loading",
                            biologicalReady: biologicalStatus === "succeeded",
                            biologicalError: biologicalStatus === "failed"
                              ? biologicalError || t("biology.loadFailed")
                              : "",
                          }
                      : id === "nephroprotection-rrt"
                        ? {
                            nephroprotectionRecord,
                            nephroprotectionLoading: nephroprotectionStatus === "loading",
                            nephroprotectionError: nephroprotectionStatus === "failed"
                              ? nephroprotectionError || t("nephro.loadFailed")
                              : "",
                          }
                      : id === "summary-plan"
                        ? {
                            summaryRecord,
                            summaryLoading: summaryStatus === "loading",
                            summaryError: summaryStatus === "failed"
                              ? summaryError || t("summary.loadFailed")
                              : "",
                          }
                      : {})}
                  />
                </div>
              </section>
            );
          })}
        </div>

        <div className="anc-footer neurologue-consultation-footer">
          <button
            type="button"
            className="anc-cancelBtn"
            onClick={() => setOpenSections((current) => {
              const next = new Set(current);
              next.delete("summary-plan");
              return next;
            })}
          >
            {t("summary.cancel")}
          </button>
          <div className="anc-footerActions">
            <button type="button" className="anc-primaryBtn" onClick={openPrescriptionPopup} disabled={isSavingConsultation}>
              {isSavingConsultation
                ? t("common.saving", { defaultValue: "Enregistrement…" })
                : t("summary.next")}
            </button>
          </div>
        </div>
        {submissionError && <p className="neurologue-consultation-error" role="alert">{submissionError}</p>}
      </div>
    </div>
  );
}
